import * as React from "react"
import Card from "@imminently/decisively-ui/Card"
import { Separator } from "@/ui/primitives/separator"
import { Label } from "@/ui/primitives/label"
import { cn } from "@/lib/utils"

interface ComponentPageProps {
  title: string
  description: string
  children: React.ReactNode
}

export function ComponentPage({ title, description, children }: ComponentPageProps) {
  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">{title}</h1>
        <p className="text-muted-foreground mt-2">{description}</p>
      </div>
      <Separator />
      {children}
    </div>
  )
}

interface ComponentSectionProps {
  title: string
  description?: string
  children: React.ReactNode
}

export function ComponentSection({ title, description, children }: ComponentSectionProps) {
  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold">{title}</h2>
        {description && <p className="text-sm text-muted-foreground mt-1">{description}</p>}
      </div>
      {children}
    </div>
  )
}

interface ComponentPreviewProps {
  children: React.ReactNode
  className?: string
}

export function ComponentPreview({ children, className }: ComponentPreviewProps) {
  return (
    <div
      className={cn(
        "flex items-center justify-center rounded-lg border border-border bg-background p-8",
        className
      )}
    >
      {children}
    </div>
  )
}

interface ControlPanelProps {
  children: React.ReactNode
}

export function ControlPanel({ children }: ControlPanelProps) {
  return (
    <Card>
      <Card.Header className="pb-3">
        <Card.Title className="text-base">Controls</Card.Title>
        <Card.Description>Adjust the component props</Card.Description>
      </Card.Header>
      <Card.Content className="space-y-4">{children}</Card.Content>
    </Card>
  )
}

interface ControlRowProps {
  label: string
  children: React.ReactNode
}

export function ControlRow({ label, children }: ControlRowProps) {
  return (
    <div className="flex items-center justify-between gap-4">
      <Label className="text-sm font-medium shrink-0">{label}</Label>
      <div className="flex-1 max-w-[200px]">{children}</div>
    </div>
  )
}

interface VariantGridProps {
  children: React.ReactNode
  columns?: number
}

export function VariantGrid({ children, columns = 4 }: VariantGridProps) {
  return (
    <div
      className={cn("grid gap-4", {
        "grid-cols-2": columns === 2,
        "grid-cols-3": columns === 3,
        "grid-cols-4": columns === 4,
        "grid-cols-5": columns === 5,
      })}
    >
      {children}
    </div>
  )
}

interface VariantItemProps {
  label: string
  children: React.ReactNode
}

export function VariantItem({ label, children }: VariantItemProps) {
  return (
    <div className="flex flex-col items-center gap-2">
      {children}
      <span className="text-xs text-muted-foreground">{label}</span>
    </div>
  )
}
